import { computed, inject, Injectable } from '@angular/core';
import { LifecycleRulesetListStore } from '../lifecycle-rules/store/lifecycle-ruleset-list.store';
import { TemplateCriteriaListStore } from '../template-criteria/store/template-criteria-list.store';
import { DynamicContentStore } from './play-talking-points/dynamic-content.store';
import { PlayValidationContext } from './play-validation.service';
import { PlayStore } from './store/play.store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { PlayTalkingPointsUsedAssetsStore } from './play-talking-points/play-talking-points.component';
import { toObservable } from '@angular/core/rxjs-interop';

type RequestStatus = 'idle' | 'pending' | 'error' | 'success';
const isLoaded = (status: RequestStatus) => status === 'success';

function areSetsEqual<T>(left: Set<T>, right: Set<T>): boolean {
    return left.size === right.size && [...left].every(value => right.has(value));
}

function areValuesEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) return true;

    if (left instanceof Date || right instanceof Date) {
        return left instanceof Date && right instanceof Date && left.getTime() === right.getTime();
    }

    if (left instanceof Set || right instanceof Set) {
        return left instanceof Set && right instanceof Set && areSetsEqual(left, right);
    }

    if (left instanceof Map || right instanceof Map) {
        return (
            left instanceof Map &&
            right instanceof Map &&
            left.size === right.size &&
            [...left].every(([key, value]) => right.has(key) && areValuesEqual(value, right.get(key)))
        );
    }

    if (Array.isArray(left) || Array.isArray(right)) {
        return (
            Array.isArray(left) &&
            Array.isArray(right) &&
            left.length === right.length &&
            left.every((value, index) => areValuesEqual(value, right[index]))
        );
    }

    if (typeof left !== 'object' || left === null || typeof right !== 'object' || right === null) {
        return false;
    }

    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    return (
        leftKeys.length === rightKeys.length &&
        leftKeys.every(
            key =>
                Object.prototype.hasOwnProperty.call(right, key) &&
                areValuesEqual((left as Record<string, unknown>)[key], (right as Record<string, unknown>)[key])
        )
    );
}

function arePlayValidationContextsEqual(
    left: PlayValidationContext | undefined,
    right: PlayValidationContext | undefined
): boolean {
    if (left === right) return true;
    if (!left || !right) return false;

    return (
        areSetsEqual(left.unavailableLifecycleRulesetIds, right.unavailableLifecycleRulesetIds) &&
        areSetsEqual(left.unavailableTemplateCriteriaIds, right.unavailableTemplateCriteriaIds) &&
        areSetsEqual(left.unavailableLibraryContentIds, right.unavailableLibraryContentIds) &&
        areSetsEqual(left.usedLibraryContentIds, right.usedLibraryContentIds) &&
        left.multipleRecommendationsEnabled === right.multipleRecommendationsEnabled &&
        areValuesEqual(left.selectedDivisionConfig, right.selectedDivisionConfig)
    );
}

/**
 * Resolves the asynchronous state play validation depends on.
 * `context` is `undefined` until every dependency has finished loading.
 */
@Injectable()
export class PlayValidationContextService {
    readonly #lifecycleRulesetListStore = inject(LifecycleRulesetListStore);
    readonly #templateCriteriaListStore = inject(TemplateCriteriaListStore);
    readonly #dynamicContentStore = inject(DynamicContentStore);
    readonly #usedAssetsStore = inject(PlayTalkingPointsUsedAssetsStore);
    readonly #playStore = inject(PlayStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);

    private selectedDivisionConfig = this.#globalLoggedInUserStore.selectedDivisionConfig;
    private multipleRecommendationsEnabled = computed<boolean | undefined>(
        () => this.selectedDivisionConfig()?.config?.mapping?.multipleRecommendations?.enabled ?? false
    );

    /**
     * The computed play validation context (all the needed state, all or nothing, depending on if everything asyncronously resolved)
     * Returns `undefined` until all dependencies are loaded and the play is available.
     */
    public context = computed<PlayValidationContext | undefined>(
        () => {
            const play = this.#playStore.play();
            const lifecycleLoaded = isLoaded(this.#lifecycleRulesetListStore.getAssets.status());
            const templateCriteriaLoaded = isLoaded(this.#templateCriteriaListStore.getAssets.status());
            const libraryContentLoaded = isLoaded(this.#dynamicContentStore.searchContentAssets.status());
            const usedLibraryContentIds = this.#usedAssetsStore.usedContentLibraryIds();
            const multipleRecommendationsEnabled = this.multipleRecommendationsEnabled();
            const selectedDivisionConfig = this.selectedDivisionConfig();

            if (
                !play ||
                !lifecycleLoaded ||
                !templateCriteriaLoaded ||
                !libraryContentLoaded ||
                multipleRecommendationsEnabled === undefined ||
                !selectedDivisionConfig
            )
                return undefined;

            const isUnavailable = (asset: { enabled?: boolean; valid?: boolean }) => !asset.enabled || !asset.valid;

            return {
                unavailableLifecycleRulesetIds: new Set(
                    this.#lifecycleRulesetListStore
                        .vm()
                        .assets.filter(isUnavailable)
                        .map(ruleset => ruleset.id)
                ),
                unavailableTemplateCriteriaIds: new Set(
                    this.#templateCriteriaListStore
                        .vm()
                        .assets.filter(isUnavailable)
                        .map(criteria => criteria.id)
                ),
                unavailableLibraryContentIds: new Set(
                    this.#dynamicContentStore
                        .libraryContent()
                        .filter(isUnavailable)
                        .map(content => content.id)
                ),
                usedLibraryContentIds: usedLibraryContentIds,
                multipleRecommendationsEnabled: multipleRecommendationsEnabled,
                selectedDivisionConfig: selectedDivisionConfig,
            };
        },
        { equal: arePlayValidationContextsEqual }
    );

    public context$ = toObservable<PlayValidationContext | undefined>(this.context);
}
